/**
 * Sessão multi-fazenda.
 *
 * Uma conta pode participar de várias fazendas com papéis diferentes. A
 * "fazenda ativa" define o escopo de tudo que o app mostra e edita. Este
 * módulo é a fonte única desse estado: guarda a lista de vínculos e a fazenda
 * ativa, deriva as chaves planas que o resto do app já lê (`role`, `fazenda`,
 * `codigoConvite`) e avisa a UI quando a fazenda ativa muda.
 *
 * Manter as chaves planas em sincronia evita reescrever cada componente que
 * lê `localStorage.getItem('role')`/('fazenda')/('codigoConvite'): elas passam
 * a refletir sempre a fazenda ativa.
 */
import { api, setFazendaAtivaId, getFazendaAtivaId } from './api';

const LS_FAZENDAS = 'fazendas';

/** Evento disparado quando a fazenda ativa (ou a lista) muda. */
export const EVENTO_FAZENDA = 'fazenda-ativa-alterada';

export function getFazendas() {
  try { return JSON.parse(localStorage.getItem(LS_FAZENDAS) || '[]'); } catch { return []; }
}

export function getFazendaAtiva() {
  const id = getFazendaAtivaId();
  if (!id) return null;
  return getFazendas().find((f) => String(f.fazendaId) === String(id)) || null;
}

export function isAdminNaAtiva() {
  const ativa = getFazendaAtiva();
  return ativa ? ativa.role === 'ADMIN' : false;
}

/** Reflete a fazenda ativa nas chaves planas que o app já consome. */
function sincronizarChavesPlanas() {
  const ativa = getFazendaAtiva();
  if (!ativa) return;
  localStorage.setItem('role', ativa.role || 'MEMBRO');
  localStorage.setItem('fazenda', ativa.fazendaNome || '');
  if (ativa.codigoConvite) localStorage.setItem('codigoConvite', ativa.codigoConvite);
  else localStorage.removeItem('codigoConvite');
}

/**
 * Grava o resultado do login/criação: lista de fazendas + qual é a ativa.
 * Usado por Login.js logo após autenticar.
 */
export function definirSessao({ fazendas, fazendaAtivaId }) {
  localStorage.setItem(LS_FAZENDAS, JSON.stringify(fazendas || []));
  const lista = fazendas || [];
  // Fazenda ativa: a indicada pelo backend, se ainda existir e for utilizável;
  // senão, a primeira ATIVA da lista.
  let ativaId = fazendaAtivaId;
  const utilizavel = (f) => f.vinculoStatus === 'ATIVO';
  if (!lista.some((f) => String(f.fazendaId) === String(ativaId) && utilizavel(f))) {
    const primeira = lista.find(utilizavel);
    ativaId = primeira ? primeira.fazendaId : null;
  }
  setFazendaAtivaId(ativaId);
  sincronizarChavesPlanas();
  window.dispatchEvent(new Event(EVENTO_FAZENDA));
}

/** Recarrega a lista de fazendas do backend (após aprovar/entrar/pagar). */
export async function recarregarFazendas() {
  const fazendas = await api.getMinhasFazendas();
  localStorage.setItem(LS_FAZENDAS, JSON.stringify(fazendas || []));
  sincronizarChavesPlanas();
  window.dispatchEvent(new Event(EVENTO_FAZENDA));
  return fazendas;
}

/**
 * Troca a fazenda ativa (sem novo login). Persiste a preferência no backend
 * (última usada) e atualiza o estado local + chaves planas.
 */
export async function trocarFazenda(fazendaId) {
  await api.ativarFazenda(fazendaId);
  setFazendaAtivaId(fazendaId);
  sincronizarChavesPlanas();
  window.dispatchEvent(new Event(EVENTO_FAZENDA));
}

/** React helper: reexecuta o callback quando a fazenda ativa muda. */
export function ouvirFazenda(callback) {
  window.addEventListener(EVENTO_FAZENDA, callback);
  return () => window.removeEventListener(EVENTO_FAZENDA, callback);
}
